import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  ArrowLeft,
  Users,
  Search,
  CheckCircle2,
  LogOut,
  LogIn,
  Palmtree,
  Clock,
  RefreshCw,
  AlertCircle,
  Sparkles,
  MapPin,
} from 'lucide-react';

export default function StaffAttendanceFlow({ onBack }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [togglingId, setTogglingId] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  const fetchStaffStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get('/api/visitors/staff-attendance/status');
      setStaff(res.data.staff || []);
    } catch (err) {
      console.error('Error fetching staff attendance status:', err);
      setError('Unable to load staff register. Please retry.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaffStatus();
    // Auto refresh every 30 seconds while on this screen
    const interval = setInterval(fetchStaffStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleToggle = async (member) => {
    if (togglingId) return; // Prevent double-tap
    setTogglingId(member.id);

    try {
      const res = await axios.post('/api/visitors/staff-attendance/toggle', {
        user_id: member.id,
        user_name: member.name,
        user_email: member.email || '',
      });

      if (res.data.success) {
        const isNowSignedIn = res.data.status === 'Signed In';
        const formattedTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        // Optimistic / direct local update
        setStaff((prev) =>
          prev.map((s) => {
            if (s.id === member.id) {
              return {
                ...s,
                status: res.data.status,
                check_in_time: res.data.check_in_time,
                check_out_time: res.data.check_out_time,
                duration_minutes: res.data.duration_minutes,
                is_allocated_site_job: res.data.is_allocated_site_job,
                site_name: res.data.site_name,
              };
            }
            return s;
          })
        );

        if (isNowSignedIn) {
          setToastMessage({
            type: 'in',
            text: res.data.message || `Welcome, ${member.name}! Signed in at ${formattedTime}.`,
          });
        } else {
          const durationStr = res.data.duration_minutes
            ? ` (${Math.floor(res.data.duration_minutes / 60)}h ${res.data.duration_minutes % 60}m)`
            : '';
          setToastMessage({
            type: 'out',
            text: `Goodbye, ${member.name}! Signed out at ${formattedTime}${durationStr}.`,
          });
        }

        // Clear toast after 4 seconds
        setTimeout(() => setToastMessage(null), 4000);
      }
    } catch (err) {
      console.error('Error toggling attendance:', err);
      setToastMessage({
        type: 'error',
        text: `Failed to update attendance for ${member.name}. Please try again.`,
      });
      setTimeout(() => setToastMessage(null), 4000);
    } finally {
      setTogglingId(null);
    }
  };

  const filteredStaff = useMemo(() => {
    if (!searchQuery.trim()) return staff;
    const q = searchQuery.toLowerCase();
    return staff.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.role && s.role.toLowerCase().includes(q)) ||
        (s.email && s.email.toLowerCase().includes(q))
    );
  }, [staff, searchQuery]);

  const onSiteCount = useMemo(() => {
    return staff.filter((s) => s.status === 'Signed In').length;
  }, [staff]);

  return (
    <div className="flex-1 flex flex-col justify-between p-6 sm:p-10 max-w-6xl mx-auto w-full">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-white/10">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={onBack}
            className="p-3 rounded-2xl bg-slate-800/80 hover:bg-slate-700 border border-white/10 text-slate-300 hover:text-white transition-all active:scale-95 shadow-md flex items-center gap-2 font-semibold text-sm"
          >
            <ArrowLeft className="w-5 h-5 text-cyan-400" />
            <span>Reception Home</span>
          </button>

          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl sm:text-3xl font-black text-white font-display">Staff Attendance</h1>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-cyan-950 text-cyan-400 border border-cyan-800">
                Single-Tap Sign In
              </span>
            </div>
            <p className="text-xs text-slate-400 font-medium mt-0.5">
              Tap your card to sign in or out. 09:00–17:00 auto-clock applies for unmarked days.
            </p>
          </div>
        </div>

        {/* Live Headcount & Refresh */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2.5 px-4 py-2 rounded-2xl bg-slate-900 border border-white/10">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
            </span>
            <span className="text-xs text-slate-400 font-medium">On Site:</span>
            <span className="text-sm font-bold text-white font-mono">
              {onSiteCount} / {staff.length}
            </span>
          </div>

          <button
            type="button"
            onClick={fetchStaffStatus}
            disabled={loading}
            className="p-2.5 rounded-2xl bg-slate-900 hover:bg-slate-800 border border-white/10 text-slate-400 hover:text-white transition-all active:scale-95"
            title="Refresh list"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-cyan-400' : ''}`} />
          </button>
        </div>
      </div>

      {/* Instant Notification Toast */}
      {toastMessage && (
        <div
          className={`my-4 p-4 rounded-2xl border text-sm font-bold flex items-center gap-3 transition-all animate-in fade-in slide-in-from-top-2 shadow-xl ${
            toastMessage.type === 'in'
              ? 'bg-emerald-950/80 border-emerald-500/50 text-emerald-200'
              : toastMessage.type === 'out'
              ? 'bg-amber-950/80 border-amber-500/50 text-amber-200'
              : 'bg-red-950/80 border-red-500/50 text-red-200'
          }`}
        >
          {toastMessage.type === 'in' ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
          ) : toastMessage.type === 'out' ? (
            <LogOut className="w-5 h-5 text-amber-400 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          )}
          <span>{toastMessage.text}</span>
        </div>
      )}

      {/* Filter / Search Bar */}
      <div className="py-4">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or role..."
            className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-slate-900/90 border border-white/10 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-cyan-500 transition-colors"
          />
        </div>
      </div>

      {/* Staff Grid */}
      <div className="flex-1 overflow-y-auto py-2">
        {loading && staff.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400 space-y-3">
            <RefreshCw className="w-8 h-8 animate-spin text-cyan-400" />
            <p className="text-sm font-medium">Loading staff members...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-400 space-y-3 text-center">
            <AlertCircle className="w-8 h-8" />
            <p className="text-sm font-medium">{error}</p>
            <button
              type="button"
              onClick={fetchStaffStatus}
              className="px-4 py-2 rounded-xl bg-slate-800 text-white text-xs font-bold border border-white/10"
            >
              Retry
            </button>
          </div>
        ) : filteredStaff.length === 0 ? (
          <div className="text-center py-20 text-slate-400 text-sm">
            No team members match "{searchQuery}".
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredStaff.map((member) => {
              const isSignedIn = member.status === 'Signed In';
              const isOnHoliday = Boolean(member.is_on_holiday);
              const isToggling = togglingId === member.id;

              // Format initials
              const initials = member.name
                .split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2);

              return (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => handleToggle(member)}
                  disabled={isToggling}
                  className={`group relative text-left rounded-3xl p-5 border-2 transition-all duration-200 flex flex-col justify-between min-h-[170px] shadow-lg active:scale-[0.98] ${
                    isToggling
                      ? 'opacity-60 pointer-events-none'
                      : isSignedIn
                      ? 'bg-gradient-to-b from-emerald-950/40 to-slate-900 border-emerald-500/50 hover:border-emerald-400 hover:shadow-emerald-950/40'
                      : isOnHoliday
                      ? 'bg-gradient-to-b from-sky-950/30 to-slate-900 border-sky-500/30 hover:border-sky-400'
                      : 'bg-gradient-to-b from-slate-850 to-slate-900 border-white/10 hover:border-cyan-500/50 hover:shadow-cyan-950/30'
                  }`}
                >
                  {/* Top card row: Initials & Status Badge */}
                  <div className="flex items-start justify-between w-full">
                    <div
                      className={`w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-base shadow-inner ${
                        isSignedIn
                          ? 'bg-emerald-500 text-slate-950'
                          : isOnHoliday
                          ? 'bg-sky-500/20 text-sky-400 border border-sky-500/30'
                          : 'bg-slate-800 text-cyan-400 border border-white/10'
                      }`}
                    >
                      {initials}
                    </div>

                    {/* Status Pill */}
                    {isSignedIn ? (
                      member.is_allocated_site_job ? (
                        <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                          <MapPin className="w-3 h-3 text-amber-400" />
                          Site Job
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                          <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></span>
                          On Site
                        </span>
                      )
                    ) : isOnHoliday ? (
                      <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-sky-500/20 text-sky-300 border border-sky-500/40">
                        <Palmtree className="w-3 h-3 text-sky-300" />
                        On Holiday
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-slate-800 text-slate-400 border border-white/5">
                        Signed Out
                      </span>
                    )}
                  </div>

                  {/* Middle: Member Name & Role */}
                  <div className="my-2.5 space-y-0.5">
                    <h2 className="text-base sm:text-lg font-bold text-white group-hover:text-cyan-300 transition-colors line-clamp-1">
                      {member.name}
                    </h2>
                    <p className="text-xs text-slate-400 font-medium capitalize line-clamp-1">
                      {member.role || 'Team Member'}
                    </p>

                    {/* Allocated Engineering Site Badge */}
                    {member.allocated_site_name ? (
                      <div className="mt-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-amber-300 bg-amber-950/60 px-2 py-0.5 rounded-lg border border-amber-500/30">
                        <MapPin className="w-3 h-3 text-amber-400 flex-shrink-0" />
                        <span className="truncate">Allocated: {member.allocated_site_name}</span>
                      </div>
                    ) : member.site_name && member.is_allocated_site_job ? (
                      <div className="mt-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-amber-300 bg-amber-950/60 px-2 py-0.5 rounded-lg border border-amber-500/30">
                        <MapPin className="w-3 h-3 text-amber-400 flex-shrink-0" />
                        <span className="truncate">Site: {member.site_name}</span>
                      </div>
                    ) : null}
                  </div>

                  {/* Bottom Action Pill / Indicator */}
                  <div className="pt-2 border-t border-white/5 flex items-center justify-between text-xs font-semibold">
                    {isSignedIn ? (
                      <>
                        <span className="text-emerald-400 flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {member.check_in_time
                            ? new Date(member.check_in_time).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : 'Active'}
                        </span>
                        <span className="text-amber-400 group-hover:underline flex items-center gap-1">
                          <LogOut className="w-3.5 h-3.5" />
                          Tap to Sign Out
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-slate-400">
                          {isOnHoliday ? 'Exempt from 9-5' : 'Away'}
                        </span>
                        <span className="text-cyan-400 group-hover:underline flex items-center gap-1">
                          <LogIn className="w-3.5 h-3.5" />
                          Tap to Sign In
                        </span>
                      </>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-cyan-400" />
          <span>Employees not logged by 17:30 are auto-recorded 09:00–17:00 (unless on holiday).</span>
        </div>
        <div>
          <span>Emergency headcount includes both on-site visitors and active staff.</span>
        </div>
      </div>
    </div>
  );
}
